import { Badge, badgeClasses, Box, Button, Paper, Stack, Typography } from '@mui/material'
import { format as dateFnsFormat, isToday } from 'date-fns'
import { ru } from 'date-fns/locale'
import { FC, MouseEvent, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    useReadApiV1NotificationsFeedUserEventReadIdxPatch,
    UserNotification,
} from '../../api/gen/base'
import { useInvalidateNotificationsFeedUserEventListQuery } from '../../api/hooks/useInvalidateNotificationsFeedUserEventListQuery'
import { useInvalidateNotificationsFeedUserEventTotal } from '../../api/hooks/useInvalidateNotificationsFeedUserEventTotal'
import { NotificationIcon } from '../NotificationIcon'

type Props = {
    compact?: boolean
    notification: UserNotification
    onClick?: VoidFunction
}

export const NotificationListItem: FC<Props> = ({
    compact,
    notification: {
        id,
        message,
        is_read,
        event: { title, code },
        action_text,
        action_url,
        created_at,
    },
    onClick,
}) => {
    const navigate = useNavigate()
    const invalidateNotificationsListQueries = useInvalidateNotificationsFeedUserEventListQuery()
    const invalidateNotificationsTotalQueries = useInvalidateNotificationsFeedUserEventTotal()

    const { mutate, isPending, isSuccess, mutateAsync } =
        useReadApiV1NotificationsFeedUserEventReadIdxPatch()

    const handleRead = () => {
        if (is_read || isPending) {
            return
        }

        mutate({ idx: id })
    }

    const handleOpenPage = (path: string) => (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        navigate(path)
        mutateAsync({ idx: id }).then(() => {
            invalidateNotificationsListQueries()
            invalidateNotificationsTotalQueries()
        })
        onClick?.()
    }

    const date = dateFnsFormat(
        created_at,
        isToday(new Date(created_at)) ? 'Сегодня, HH:mm' : 'dd MMMM, HH:mm',
        { locale: ru },
    )

    const actionButton =
        action_text && action_url ? (
            <Button
                sx={{
                    maxWidth: 'fit-content',
                    width: '-webkit-fill-available',
                }}
                variant="contained"
                onClick={handleOpenPage(action_url)}>
                {action_text}
            </Button>
        ) : null

    useEffect(() => {
        if (isSuccess) {
            invalidateNotificationsListQueries()
            invalidateNotificationsTotalQueries()
        }
    }, [isSuccess])

    return (
        <Paper
            onClick={handleRead}
            sx={{
                display: 'flex',
                flexDirection: compact ? 'column' : 'row',
                gap: compact ? 0 : '12px',
                width: '100%',
                minWidth: '60%',
                borderRadius: '16px',
                boxShadow: 'none',
                padding: '16px',
                border: is_read ? '1px solid #F4F4F6' : 'none',
                backgroundColor: is_read ? '#FFF' : '#F9FAFB',
                cursor: is_read ? 'default' : 'pointer',
            }}>
            <Stack direction="row" gap={1} justifyContent="space-between" width="100%">
                <Stack direction={compact ? 'column' : 'row'} gap={1} width="100%">
                    <Stack
                        direction="row"
                        alignItems={compact ? 'center' : 'flex-start'}
                        justifyContent="space-between">
                        <Stack direction="row" alignItems="center" gap={1}>
                            <Badge
                                badgeContent={0}
                                showZero={!is_read}
                                color="error"
                                variant="dot"
                                sx={{
                                    [`.${badgeClasses.badge}`]: {
                                        border: compact ? '1px solid #FFFFFF' : '1px solid #EFF6FF',
                                        p: 0,
                                        borderRadius: '50%',
                                        fontSize: '10px',
                                        minWidth: is_read ? 0 : '12px',
                                        height: is_read ? 0 : '12px',
                                        transform: compact
                                            ? 'scale(1) translate(15%, -15%)'
                                            : 'scale(1) translate(-50%, 50%)',
                                    },
                                }}>
                                <Box
                                    sx={{
                                        width: compact ? '24px' : '40px',
                                        height: compact ? '24px' : '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#EFF6FF',
                                        borderRadius: '50%',
                                    }}>
                                    <NotificationIcon compact={compact} eventCode={code} />
                                </Box>
                            </Badge>
                            {compact && (
                                <Typography variant="caption2" color="#9195A1">
                                    {date}
                                </Typography>
                            )}
                        </Stack>
                    </Stack>
                    <Stack gap={1}>
                        <Stack gap="4px" pr={compact ? 1 : 0}>
                            <Typography variant="h6">{title}</Typography>
                            <Typography variant="caption2">{message}</Typography>
                        </Stack>
                        {!compact ? (
                            <Typography variant="caption2" color="#9195A1">
                                {date}
                            </Typography>
                        ) : (
                            actionButton
                        )}
                    </Stack>
                </Stack>
                {!compact && actionButton}
            </Stack>
        </Paper>
    )
}
